// ============================================================
// Token studente (WP-13e, spec 2026-10-11-identita-studente-sessione §5.5, §7.1, §9)
// ============================================================
// Un solo posto per leggere, scrivere e cancellare il token studente sul client.
// Regole (dati di minori):
//  - il token NON va mai in URL (query), ne' nei log, ne' in un messaggio di errore;
//    al massimo nel FRAMMENTO dell'URL di lancio (`#tl4k_st=`), che non arriva al server e
//    non finisce nel Referer, e viene tolto con history.replaceState alla prima lettura;
//  - archivio di default: sessionStorage (una scheda, un token). TechyWeb, che per spec
//    sopravvive alla ricarica come `techyweb_tl4k_session`, configura localStorage;
//  - nessuna verifica della firma qui: il client non ha il secret. La decodifica serve solo a
//    sapere tipo (`typ`) e scadenza (`exp`) per non spedire un token gia' scaduto;
//  - con nessun token il comportamento dei client e' IDENTICO a prima (nessun header, nessuna
//    chiave nell'auth del socket): in produzione STUDENT_TOKEN_MODE e' off/issue.
// ============================================================

export const STUDENT_TOKEN_HEADER = "X-TL4K-Student";
export const STUDENT_APP_HEADER = "X-TL4K-App";
export const STUDENT_TOKEN_FRAGMENT_KEY = "tl4k_st";
export const STUDENT_TOKEN_DEFAULT_KEY = "tl4k_student_token";

const MAX_TOKEN_LENGTH = 4096;
const TOKEN_SHAPE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

export type StudentTokenKind = "participant" | "proposal";

export interface StudentTokenMeta {
  typ: StudentTokenKind | null;
  /** Secondi Unix (come il claim `exp`). */
  exp: number | null;
  sub: string | null;
  sid: string | null;
  rsid: string | null;
}

export interface StudentTokenStoreConfig {
  /** `session` (default) o `local`. */
  storage?: "session" | "local";
  /** Chiave di archivio. Default `tl4k_student_token`. */
  key?: string;
  /** Id app inviato in `X-TL4K-App` (solo informativo: il modo lo decide il server dalla rotta). */
  appId?: string | null;
}

let storageKind: "session" | "local" = "session";
let storageKey = STUDENT_TOKEN_DEFAULT_KEY;
let appId: string | null = null;
/** Copia in memoria: l'archivio puo' mancare (finestra privata, partizionamento). */
let memoryToken: string | null = null;
let fragmentChecked = false;
const listeners = new Set<() => void>();

export function configureStudentTokenStore(config: StudentTokenStoreConfig): void {
  if (config.storage) storageKind = config.storage;
  if (config.key) storageKey = config.key;
  if (config.appId !== undefined) appId = config.appId?.trim() || null;
}

export function getStudentAppId(): string | null {
  return appId;
}

function backend(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return storageKind === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function readRaw(): string | null {
  try {
    const v = backend()?.getItem(storageKey);
    if (v) return v;
  } catch {
    // archivio non disponibile: si usa la memoria
  }
  return memoryToken;
}

function notify(): void {
  listeners.forEach((cb) => {
    try {
      cb();
    } catch {
      // un ascoltatore che lancia non deve fermare gli altri
    }
  });
}

function base64UrlDecode(segment: string): string | null {
  try {
    const padded = segment.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (segment.length % 4)) % 4);
    if (typeof atob === "function") {
      const bin = atob(padded);
      const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
      return new TextDecoder().decode(bytes);
    }
    return null;
  } catch {
    return null;
  }
}

/** Legge i claim senza verificare la firma. Solo per tipo e scadenza: mai per decisioni di accesso. */
export function decodeStudentTokenMeta(token: string): StudentTokenMeta | null {
  if (!TOKEN_SHAPE.test(token) || token.length > MAX_TOKEN_LENGTH) return null;
  const json = base64UrlDecode(token.split(".")[1]);
  if (!json) return null;
  try {
    const c = JSON.parse(json) as Record<string, unknown>;
    const str = (v: unknown) => (typeof v === "string" && v ? v : null);
    return {
      typ: c.typ === "participant" || c.typ === "proposal" ? c.typ : null,
      exp: typeof c.exp === "number" && Number.isFinite(c.exp) ? c.exp : null,
      sub: str(c.sub),
      sid: str(c.sid),
      rsid: str(c.rsid),
    };
  } catch {
    return null;
  }
}

function isExpired(token: string, nowMs: number = Date.now()): boolean {
  const meta = decodeStudentTokenMeta(token);
  return meta?.exp != null && meta.exp * 1000 <= nowMs;
}

/**
 * Prende il token dal frammento dell'URL (`#tl4k_st=<token>`), lo salva e toglie il frammento
 * dalla barra con history.replaceState. Ritorna il token letto o null. Mai in query string.
 */
export function consumeStudentTokenFromFragment(): string | null {
  fragmentChecked = true;
  if (typeof window === "undefined") return null;
  let hash = "";
  try {
    hash = window.location.hash || "";
  } catch {
    return null;
  }
  const match = new RegExp(`(^#|&)${STUDENT_TOKEN_FRAGMENT_KEY}=([^&]*)`).exec(hash);
  if (!match) return null;

  // Toglie il frammento dalla barra comunque: un token malformato non deve restare nell'URL.
  try {
    const rest = hash.replace(match[0], match[1] === "&" ? "" : "#").replace(/^#&/, "#");
    const cleanHash = rest === "#" ? "" : rest;
    const clean = new URL(window.location.href);
    clean.hash = cleanHash;
    window.history.replaceState(window.history.state, "", clean.toString());
  } catch {
    // replaceState non disponibile: il token resta in barra solo localmente (frammento, non spedito)
  }

  let token: string;
  try {
    token = decodeURIComponent(match[2]);
  } catch {
    return null;
  }
  return setStudentToken(token) ? token : null;
}

/** Token corrente, o null. Scarta (e cancella) un token gia' scaduto. */
export function getStudentToken(): string | null {
  // Il frammento di lancio vince sul token gia' in archivio (un nuovo ingresso sovrascrive il vecchio).
  if (!fragmentChecked) consumeStudentTokenFromFragment();
  const raw = readRaw();
  if (!raw) return null;
  if (!TOKEN_SHAPE.test(raw) || isExpired(raw)) {
    clearStudentToken();
    return null;
  }
  return raw;
}

/** `participant`, `proposal` o null (nessun token o tipo illeggibile). */
export function getStudentTokenKind(): StudentTokenKind | null {
  const t = getStudentToken();
  return t ? (decodeStudentTokenMeta(t)?.typ ?? null) : null;
}

/** Salva il token. Ritorna false (senza salvare) se ha forma implausibile o e' gia' scaduto. */
export function setStudentToken(token: string): boolean {
  if (typeof token !== "string" || !TOKEN_SHAPE.test(token) || token.length > MAX_TOKEN_LENGTH) return false;
  if (isExpired(token)) return false;
  memoryToken = token;
  try {
    backend()?.setItem(storageKey, token);
  } catch {
    // resta in memoria
  }
  notify();
  return true;
}

export function clearStudentToken(): void {
  const had = memoryToken !== null || (() => { try { return !!backend()?.getItem(storageKey); } catch { return false; } })();
  memoryToken = null;
  try {
    backend()?.removeItem(storageKey);
  } catch {
    // niente da fare
  }
  if (had) notify();
}

/** Avvisa quando il token cambia o viene cancellato. Ritorna la funzione per smettere. */
export function onStudentTokenChange(callback: () => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

/** Header `X-TL4K-Student`, o oggetto vuoto: con nessun token la richiesta resta quella di sempre. */
export function studentAuthHeaders(): Record<string, string> {
  const t = getStudentToken();
  return t ? { [STUDENT_TOKEN_HEADER]: t } : {};
}

/** Frammento da accodare all'URL di lancio di un'app incorporata (`#tl4k_st=...`) oppure stringa vuota. */
export function buildStudentTokenFragment(token: string | null = getStudentToken()): string {
  if (!token || !TOKEN_SHAPE.test(token)) return "";
  return `${STUDENT_TOKEN_FRAGMENT_KEY}=${encodeURIComponent(token)}`;
}

/** Solo test: riporta il modulo allo stato iniziale. */
export function __resetStudentTokenForTests(): void {
  storageKind = "session";
  storageKey = STUDENT_TOKEN_DEFAULT_KEY;
  appId = null;
  memoryToken = null;
  fragmentChecked = false;
  listeners.clear();
}
